/**
 * Portal theme save → "is it on the router yet?"
 *
 * A hybrid Ruijie/OrangePi serves the portal itself and only learns a new
 * theme on its next heartbeat (about 30 s, up to a minute). Until then the
 * phone still shows the old look and the panel used to say nothing. Cloud
 * vendos (MikroTik, cloud Ruijie) load the portal from the cloud, so a save
 * is live at once. (Ray, 2026-09-15)
 *
 * Purely derived from record timestamps, like renameProgress.
 */
export type ThemeStage = 'router-wait' | 'done' | 'stuck' | null;

const STUCK_AFTER_MS = 180_000; // router silent this long after a save
const SHOW_DONE_FOR_MS = 5 * 60_000;

export function portalThemeStage(
   device: {
      portalConfigSavedAt?: string | Date | null;
      router?: {
         localMode?: boolean | null;
         lastSeenAt?: string | Date | null;
         platform?: string | null;
      };
      /** The NodeMCU's last check-in. */
      lastHeartbeat?: string | Date | null;
   },
   now: number = Date.now()
): ThemeStage {
   const at = device.portalConfigSavedAt ? new Date(device.portalConfigSavedAt).getTime() : 0;
   if (!at) return null;
   const elapsed = now - at;
   if (elapsed < 0 || elapsed > SHOW_DONE_FOR_MS) return null;
   // Not a hybrid router: the portal comes from the cloud, live already.
   if (device.router?.localMode !== true) return 'done';
   // A MikroTik Hybrid's portal is served by its NODEMCU, which fetches the new
   // look on its next check-in (every few seconds). The hEX itself never checks
   // in, so waiting for it showed "has not checked in" for ever (bench
   // 2026-10-02, with the change already on the phone).
   const seenField =
      device.router?.platform === 'mikrotik' ? device.lastHeartbeat : device.router?.lastSeenAt;
   const seen = seenField ? new Date(seenField).getTime() : 0;
   if (seen > at) return 'done';
   return elapsed >= STUCK_AFTER_MS ? 'stuck' : 'router-wait';
}

export function portalThemeText(stage: ThemeStage, elapsedMs: number): string {
   switch (stage) {
      case 'router-wait': {
         const s = Math.max(0, Math.round(elapsedMs / 1000));
         return `Saved. Sending to the router… ${s}s (the router picks it up within about a minute)`;
      }
      case 'done':
         return 'Live on the portal. Customers see it on their next reload.';
      case 'stuck':
         return 'Saved, but the router has not checked in since. It will pick this up as soon as it is back online.';
      default:
         return '';
   }
}

/**
 * A sub-vendo has no router of its own: its portal lives on the PARENT's
 * router, so "has the router picked it up" must be judged by the parent's
 * check-in. Bench 2026-09-23: a theme saved on MINIPC SUB - 22 sat on "the
 * router has not checked in since" for ever, while the parent checked in
 * every 30 s. Returns the device to judge; the main is judged as itself.
 */
export function themeProgressDevice<T extends { parent?: string | null; router?: unknown }>(
   device: T,
   parentRouter: T['router'] | null | undefined
): T {
   if (!device.parent || !parentRouter) return device;
   return { ...device, router: parentRouter };
}
